/**
 * Снимок строк — решение №1 спеки (§5.4, принцип П2).
 *
 * Фильтры, сортировка и все счётчики считаются по ОПУБЛИКОВАННЫМ значениям.
 * Набор и порядок строк замораживаются и пересчитываются только по явному
 * поводу: смена фильтра, сортировки, канала, представления, кнопка «Обновить»,
 * публикация пакета. Строка, которую человек только что заполнил, не исчезает
 * под курсором — иначе индексы виртуального списка поедут посреди серии
 * «Enter → следующая строка» и `]` / `[` перестанут работать.
 */
import { useCallback, useMemo, useState } from 'react'
import type { Brand } from '@shared/types.ts'

export interface RowSnapshotInput {
  /** Уже отфильтрованный и отсортированный массив — порядок показа. */
  visibleBrands: readonly Brand[]
  /** Любое изменение подписи пересчитывает снимок (фильтры, сортировка, канал). */
  signature: string
  /** Ревизия опубликованных данных: публикация пакета тоже пересчитывает снимок. */
  revision: number
  /** Бренды, созданные в черновике: показываются всегда, даже вне фильтра (§4.3). */
  draftBrandIds: ReadonlySet<number>
  brandsById: ReadonlyMap<number, Brand>
  /**
   * Расхождение снимка с фильтром, посчитанное по ЧЕРНОВЫМ значениям.
   * Модуль фильтров умеет это точно (у него есть предикат фильтра), и его
   * расчёт главнее: здесь остаётся запасной — разница множеств.
   */
  driftIds?: ReadonlySet<number> | undefined
}

export interface RowSnapshot {
  /** Строки в замороженном порядке. */
  brands: Brand[]
  /** Строки снимка, которые под текущий фильтр больше не подходят (§5.4). */
  driftIds: ReadonlySet<number>
  driftCount: number
  /** Новые бренды из черновика, показанные вне фильтра. */
  outsideFilterIds: ReadonlySet<number>
  /** Явное обновление снимка — ссылка «Обновить». */
  refresh: () => void
}

interface Frozen {
  ids: number[]
  signature: string
  revision: number
  /** Счётчик ручных обновлений: нужен, чтобы «Обновить» заново запустил расчёт. */
  nonce: number
}

export function useRowSnapshot(input: RowSnapshotInput): RowSnapshot {
  const { visibleBrands, signature, revision, draftBrandIds, brandsById } = input

  const freeze = useCallback(
    (nonce: number): Frozen => ({
      ids: visibleBrands.map((brand) => brand.id),
      signature,
      revision,
      nonce,
    }),
    [visibleBrands, signature, revision],
  )

  const [frozen, setFrozen] = useState<Frozen>(() => freeze(0))

  // Пересчёт в фазе рендера, а не в эффекте: иначе кадр показал бы старый
  // порядок строк под новым фильтром.
  if (frozen.signature !== signature || frozen.revision !== revision) {
    setFrozen(freeze(frozen.nonce))
  }

  const refresh = useCallback(() => {
    setFrozen((previous) => ({
      ids: visibleBrands.map((brand) => brand.id),
      signature,
      revision,
      nonce: previous.nonce + 1,
    }))
  }, [visibleBrands, signature, revision])

  return useMemo(() => {
    const visibleIds = new Set(visibleBrands.map((brand) => brand.id))
    const inSnapshot = new Set(frozen.ids)

    const brands: Brand[] = []
    for (const id of frozen.ids) {
      const brand = brandsById.get(id)
      if (brand) brands.push(brand)
    }

    // Новый бренд из черновика виден всегда — иначе человек создал бренд, а он исчез.
    const outsideFilterIds = new Set<number>()
    for (const id of draftBrandIds) {
      if (inSnapshot.has(id)) continue
      const brand = brandsById.get(id)
      if (!brand) continue
      brands.unshift(brand)
      outsideFilterIds.add(id)
    }

    const fallback = new Set<number>()
    for (const id of frozen.ids) if (!visibleIds.has(id)) fallback.add(id)
    const driftIds = input.driftIds ?? fallback

    return { brands, driftIds, driftCount: driftIds.size, outsideFilterIds, refresh }
  }, [frozen, visibleBrands, brandsById, draftBrandIds, refresh, input.driftIds])
}
